/**
 * The data the **question bank** and **option list** screens run on until survey-api grows
 * endpoints for them — SR-123's tickets 2 and 3.
 *
 * Two sets of fixtures have already left this file, each when the endpoint behind it
 * shipped: the response fixtures with SR-117's oversight surface, and the account fixtures
 * with SR-124's list. The version fixtures below are the next to go — the version *list* is
 * real now (`GET /admin/definitions/surveys/{code}/versions`), and what is left here is the
 * cross-version diff, which no endpoint answers.
 *
 * Written against the real vocabulary rather than made up: survey codes and cycle codes as
 * the seeders publish them (`RIA_FIRM`, `FA`, `FSP`, `TEAMS`, cycle `2027`), usage codes in
 * the spreadsheets' own form (`RIA-PC-007`, survey content v1's), bank slugs as the design writes them
 * (`partners.has_partner`), field types from the respondent contract's `FieldTypeCode`, and
 * option list codes from the design's own examples (`yes_no`, `designations`).
 *
 * Kept small on purpose — enough for every screen to show a real shape (a list whose
 * versions drifted, a published version beside a draft) without pretending to be a database
 * dump.
 *
 * Dates are literals, not `Date.now()` arithmetic: a fixture that moves with the clock makes
 * a screenshot unreproducible and a stale-looking column depend on when the reader opened
 * it.
 */

import type { IBankQuestion, ISurveyVersion } from './contracts';

export const QUESTIONS: IBankQuestion[] = [
  {
    questionId: 'q-firm-name',
    code: 'firm.legal_name',
    subjectKind: 'org',
    fieldTypeCode: 'text_short',
    defaultLabel: 'Legal firm name',
    defaultOptionListCode: null,
    origin: 'spec',
    notes: null,
    placements: [
      {
        surveyCode: 'RIA_FIRM',
        versionNo: 2,
        versionStatus: 'published',
        usageCode: 'RIA-FP-001',
        sectionCode: 'S1',
        labelOverride: null
      },
      {
        surveyCode: 'RIA_FIRM',
        versionNo: 3,
        versionStatus: 'draft',
        usageCode: 'RIA-FP-001',
        sectionCode: 'S1',
        labelOverride: null
      },
      {
        surveyCode: 'TEAMS',
        versionNo: 1,
        versionStatus: 'published',
        usageCode: 'TEAM-RG-003',
        sectionCode: 'AC',
        labelOverride: 'Team’s firm, as registered'
      }
    ]
  },
  {
    questionId: 'q-partners-has',
    code: 'partners.has_partner',
    subjectKind: 'org',
    fieldTypeCode: 'radio',
    defaultLabel: 'Does the firm have partners?',
    defaultOptionListCode: 'yes_no',
    origin: 'spec',
    notes: 'Gates the partner grid in S1; the grid is `minted` from this answer.',
    placements: [
      {
        surveyCode: 'RIA_FIRM',
        versionNo: 2,
        versionStatus: 'published',
        usageCode: 'RIA-CE-004',
        sectionCode: 'S1',
        labelOverride: null
      },
      {
        surveyCode: 'RIA_FIRM',
        versionNo: 3,
        versionStatus: 'draft',
        usageCode: 'RIA-CE-004',
        sectionCode: 'S1',
        labelOverride: null
      }
    ]
  },
  {
    questionId: 'q-partners-count',
    code: 'partners.count',
    subjectKind: 'org',
    fieldTypeCode: 'int',
    defaultLabel: 'How many partners?',
    defaultOptionListCode: null,
    origin: 'minted',
    notes: 'No spreadsheet ID — minted as the count that materializes the partner rows.',
    placements: [
      {
        surveyCode: 'RIA_FIRM',
        versionNo: 2,
        versionStatus: 'published',
        usageCode: 'RIA-PC-001',
        sectionCode: 'S1',
        labelOverride: null
      },
      {
        surveyCode: 'RIA_FIRM',
        versionNo: 3,
        versionStatus: 'draft',
        usageCode: 'RIA-PC-001',
        sectionCode: 'S1',
        labelOverride: null
      }
    ]
  },
  {
    questionId: 'q-designations',
    code: 'advisor.designations',
    subjectKind: 'person',
    fieldTypeCode: 'multi_select',
    defaultLabel: 'Professional designations held',
    defaultOptionListCode: 'designations',
    origin: 'spec',
    notes: 'RIA_FIRM pins v2 of the list, FA still pins v1 — see the option list’s drift.',
    placements: [
      {
        surveyCode: 'RIA_FIRM',
        versionNo: 2,
        versionStatus: 'published',
        usageCode: 'RIA-OS-008',
        sectionCode: 'S4',
        labelOverride: null
      },
      {
        surveyCode: 'RIA_FIRM',
        versionNo: 3,
        versionStatus: 'draft',
        usageCode: 'RIA-OS-008',
        sectionCode: 'S4',
        labelOverride: null
      },
      {
        surveyCode: 'FA',
        versionNo: 1,
        versionStatus: 'published',
        usageCode: 'FA-CH-005',
        sectionCode: 'S7',
        labelOverride: 'Designations you personally hold'
      },
      {
        surveyCode: 'FSP',
        versionNo: 1,
        versionStatus: 'published',
        usageCode: 'FSP-CH-003',
        sectionCode: 'S3',
        labelOverride: null
      }
    ]
  },
  {
    questionId: 'q-aum-total',
    code: 'firm.aum_total',
    subjectKind: 'org',
    fieldTypeCode: 'currency',
    defaultLabel: 'Total assets under management',
    defaultOptionListCode: null,
    origin: 'spec',
    notes: 'Reconciled against the per-channel breakdown by a soft rule.',
    placements: [
      {
        surveyCode: 'RIA_FIRM',
        versionNo: 2,
        versionStatus: 'published',
        usageCode: 'RIA-PM-001',
        sectionCode: 'S2',
        labelOverride: null
      },
      {
        surveyCode: 'RIA_FIRM',
        versionNo: 3,
        versionStatus: 'draft',
        usageCode: 'RIA-PM-001',
        sectionCode: 'S2',
        labelOverride: 'Total assets under management (firm-wide)'
      },
      {
        surveyCode: 'TEAMS',
        versionNo: 1,
        versionStatus: 'published',
        usageCode: 'TEAM-PM-003',
        sectionCode: 'AC',
        labelOverride: null
      }
    ]
  },
  {
    questionId: 'q-aum-by-channel',
    code: 'firm.aum_by_channel',
    subjectKind: 'org',
    fieldTypeCode: 'grid',
    defaultLabel: 'Assets under management by channel',
    defaultOptionListCode: 'ria_ownership',
    origin: 'spec',
    notes: null,
    placements: [
      {
        surveyCode: 'RIA_FIRM',
        versionNo: 2,
        versionStatus: 'published',
        usageCode: 'RIA-IA-002',
        sectionCode: 'S2',
        labelOverride: null
      },
      {
        surveyCode: 'RIA_FIRM',
        versionNo: 3,
        versionStatus: 'draft',
        usageCode: 'RIA-IA-002',
        sectionCode: 'S2',
        labelOverride: null
      }
    ]
  },
  {
    questionId: 'q-services',
    code: 'firm.services_offered',
    subjectKind: 'org',
    fieldTypeCode: 'multi_select',
    defaultLabel: 'Advisory services offered',
    defaultOptionListCode: 'advisory_services',
    origin: 'spec',
    notes: null,
    placements: [
      {
        surveyCode: 'RIA_FIRM',
        versionNo: 2,
        versionStatus: 'published',
        usageCode: 'RIA-SV-001',
        sectionCode: 'S5',
        labelOverride: null
      },
      {
        surveyCode: 'RIA_FIRM',
        versionNo: 3,
        versionStatus: 'draft',
        usageCode: 'RIA-SV-001',
        sectionCode: 'S6',
        labelOverride: null
      },
      {
        surveyCode: 'TEAMS',
        versionNo: 1,
        versionStatus: 'published',
        usageCode: 'TEAM-SV-001',
        sectionCode: 'SV',
        labelOverride: null
      }
    ]
  },
  {
    questionId: 'q-office-count',
    code: 'firm.office_count',
    subjectKind: 'org',
    fieldTypeCode: 'int',
    defaultLabel: 'Number of offices',
    defaultOptionListCode: null,
    origin: 'external',
    notes: 'Sourced from SEC Form ADV; prefilled and locked, never respondent-writable.',
    placements: [
      {
        surveyCode: 'RIA_FIRM',
        versionNo: 2,
        versionStatus: 'published',
        usageCode: 'RIA-FP-013',
        sectionCode: 'S1',
        labelOverride: null
      },
      {
        surveyCode: 'RIA_FIRM',
        versionNo: 3,
        versionStatus: 'draft',
        usageCode: 'RIA-FP-013',
        sectionCode: 'S1',
        labelOverride: null
      }
    ]
  },
  {
    questionId: 'q-team-structure',
    code: 'team.structure',
    subjectKind: 'team',
    fieldTypeCode: 'radio',
    defaultLabel: 'How is the team structured?',
    defaultOptionListCode: 'yes_no',
    origin: 'spec',
    notes: 'Placeholder option list — the real one is pending the Teams workbook review.',
    placements: [
      {
        surveyCode: 'TEAMS',
        versionNo: 1,
        versionStatus: 'published',
        usageCode: 'TEAM-TS-001',
        sectionCode: 'AC',
        labelOverride: null
      }
    ]
  },
  {
    questionId: 'q-years-experience',
    code: 'advisor.years_experience',
    subjectKind: 'person',
    fieldTypeCode: 'int',
    defaultLabel: 'Years of industry experience',
    defaultOptionListCode: null,
    origin: 'spec',
    notes: null,
    placements: [
      {
        surveyCode: 'FA',
        versionNo: 1,
        versionStatus: 'published',
        usageCode: 'FA-CH-002',
        sectionCode: 'S2',
        labelOverride: null
      },
      {
        surveyCode: 'FSP',
        versionNo: 1,
        versionStatus: 'published',
        usageCode: 'FSP-CH-001',
        sectionCode: 'S1',
        labelOverride: null
      }
    ]
  },
  {
    questionId: 'q-retention',
    code: 'firm.client_retention_rate',
    subjectKind: 'org',
    fieldTypeCode: 'percent',
    defaultLabel: 'Client retention rate',
    defaultOptionListCode: null,
    origin: 'computed',
    notes: 'Computed from the retained/lost counts; never asked directly.',
    placements: [
      {
        surveyCode: 'RIA_FIRM',
        versionNo: 3,
        versionStatus: 'draft',
        usageCode: 'RIA-OS-012',
        sectionCode: 'S6',
        labelOverride: null
      }
    ]
  },
  {
    questionId: 'q-succession',
    code: 'firm.succession_plan',
    subjectKind: 'org',
    fieldTypeCode: 'text_area',
    defaultLabel: 'Describe the firm’s succession plan',
    defaultOptionListCode: null,
    origin: 'spec',
    notes: 'New for the 2028 cycle; lives only in the draft version so far.',
    placements: [
      {
        surveyCode: 'RIA_FIRM',
        versionNo: 3,
        versionStatus: 'draft',
        usageCode: 'RIA-OS-013',
        sectionCode: 'S6',
        labelOverride: null
      }
    ]
  }
];

export const VERSIONS: ISurveyVersion[] = [
  {
    surveyVersionId: 'sv-ria-3',
    surveyCode: 'RIA_FIRM',
    surveyName: 'RIA Firm Survey',
    versionNo: 3,
    status: 'draft',
    vocabVersion: 2,
    publishedAt: null,
    publishedBy: null,
    notes: 'Succession-plan section and the computed retention rate, for the 2028 cycle.',
    counts: { sections: 13, usages: 148, rules: 61 },
    pinnedInCycles: []
  },
  {
    surveyVersionId: 'sv-ria-2',
    surveyCode: 'RIA_FIRM',
    surveyName: 'RIA Firm Survey',
    versionNo: 2,
    status: 'published',
    vocabVersion: 2,
    publishedAt: '2026-08-14T12:30:00Z',
    publishedBy: 'rankings.admin@shookresearch.com',
    notes: 'Designations list moved to v2; partner grid reworked as repeat rows.',
    counts: { sections: 12, usages: 146, rules: 58 },
    pinnedInCycles: ['2027']
  },
  {
    surveyVersionId: 'sv-ria-1',
    surveyCode: 'RIA_FIRM',
    surveyName: 'RIA Firm Survey',
    versionNo: 1,
    status: 'retired',
    vocabVersion: 1,
    publishedAt: '2026-08-04T09:00:00Z',
    publishedBy: 'system@shookresearch.com',
    notes: 'First transcription of the 2027 workbook.',
    counts: { sections: 12, usages: 141, rules: 54 },
    pinnedInCycles: ['legacy']
  },
  {
    surveyVersionId: 'sv-fa-1',
    surveyCode: 'FA',
    surveyName: 'Financial Advisor Survey',
    versionNo: 1,
    status: 'published',
    vocabVersion: 2,
    publishedAt: '2026-08-13T15:10:00Z',
    publishedBy: 'system@shookresearch.com',
    notes: null,
    counts: { sections: 9, usages: 112, rules: 37 },
    pinnedInCycles: ['2027']
  },
  {
    surveyVersionId: 'sv-fsp-1',
    surveyCode: 'FSP',
    surveyName: 'Financial Security Professional Survey',
    versionNo: 1,
    status: 'published',
    vocabVersion: 2,
    publishedAt: '2026-08-17T10:05:00Z',
    publishedBy: 'system@shookresearch.com',
    notes: null,
    counts: { sections: 8, usages: 96, rules: 29 },
    pinnedInCycles: ['2027']
  },
  {
    surveyVersionId: 'sv-teams-1',
    surveyCode: 'TEAMS',
    surveyName: 'Wealth Management Teams Survey',
    versionNo: 1,
    status: 'published',
    vocabVersion: 2,
    publishedAt: '2026-08-17T16:40:00Z',
    publishedBy: 'system@shookresearch.com',
    notes: 'Two paths: WIRES and INDEPENDENT.',
    counts: { sections: 7, usages: 88, rules: 24 },
    pinnedInCycles: ['2027']
  }
];


/** Field types a bank question may take, for the editor's picker — the respondent
 *  contract's own `FieldTypeCode` union, which is what publish validates params against. */
